import React, { useState, useRef, useEffect } from 'react';
import { RefreshCw, ArrowRight, Upload, Smartphone, MoreVertical, Pencil, Trash2, Loader2, X, Sparkles } from 'lucide-react';
import { apiGet, apiDelete, apiPost as apiPostUtil } from '../utils/api';
import { toast } from 'sonner';
import Lottie from 'lottie-react';
import aiBrainAnimation from '../assets/ai-brain.json';

const PRIORITIES = [
  { value: 'urgent', label: 'Urgent', cls: 'bg-red-500 text-white' },
  { value: 'high', label: 'High', cls: 'bg-orange-500 text-white' },
  { value: 'medium', label: 'Medium', cls: 'bg-yellow-400 text-black' },
  { value: 'low', label: 'Low', cls: 'bg-green-500 text-white' },
];

const ATTACH_OPTIONS = [
  { key: 'upload', label: 'Upload from device', icon: Upload },
  { key: 'mobile', label: 'Mobile device', icon: Smartphone },
  { key: 'gdrive', label: 'Google Drive', img: 'https://upload.wikimedia.org/wikipedia/commons/1/12/Google_Drive_icon_%282020%29.svg' },
  { key: 'dropbox', label: 'Dropbox', img: 'https://upload.wikimedia.org/wikipedia/commons/7/78/Dropbox_Icon.svg' },
  { key: 'onedrive', label: 'OneDrive', img: 'https://upload.wikimedia.org/wikipedia/commons/3/3c/Microsoft_Office_OneDrive_%282019%E2%80%93present%29.svg' },
];

const ICON_CATEGORIES = [
  { name: 'Questions', icons: [
    { path: '/icons/default-question.svg', label: 'Default' },
    { path: '/icons/questions/question.svg', label: 'Question' },
    { path: '/icons/questions/question2.svg', label: 'Question 2' },
    { path: '/icons/questions/questions.svg', label: 'Questions' },
    { path: '/icons/questions/light-bulb.svg', label: 'Light Bulb' },
    { path: '/icons/questions/light-bulb-speech-bubble.svg', label: 'Speech' },
  ]},
  { name: 'Business', icons: [
    { path: '/icons/business/analytical-thinking.svg', label: 'Thinking' },
    { path: '/icons/business/business-idea.svg', label: 'Idea' },
    { path: '/icons/business/business-and-finance.svg', label: 'Finance' },
    { path: '/icons/business/coin-stack.svg', label: 'Coins' },
    { path: '/icons/business/deal.svg', label: 'Deal' },
    { path: '/icons/business/focus.svg', label: 'Focus' },
    { path: '/icons/business/innovation.svg', label: 'Innovation' },
    { path: '/icons/business/planning.svg', label: 'Planning' },
    { path: '/icons/business/target.svg', label: 'Target' },
    { path: '/icons/business/task-planning.svg', label: 'Task' },
  ]},
  { name: 'Investment', icons: [
    { path: '/icons/investment/investment.svg', label: 'Investment' },
    { path: '/icons/investment/investment-growth.svg', label: 'Growth' },
    { path: '/icons/investment/investment-chart.svg', label: 'Chart' },
    { path: '/icons/investment/index-fund.svg', label: 'Index' },
    { path: '/icons/investment/value.svg', label: 'Value' },
  ]},
];

export default function CardBackSide({ card, board, setBoard, setFlippedCards, setSelectedCard, apiPatch, apiPost, API_BASE }) {
  const [editingDesc, setEditingDesc] = useState(false);
  const [desc, setDesc] = useState(card.description || '');
  const [localItems, setLocalItems] = useState(card.card_items || []);
  const [itemText, setItemText] = useState('');
  const [selectedIcon, setSelectedIcon] = useState('/icons/business/target.svg');
  const [showPriority, setShowPriority] = useState(false);
  const [showCalendar, setShowCalendar] = useState(false);
  const [showAttach, setShowAttach] = useState(false);
  const [showIconDeck, setShowIconDeck] = useState(false);
  const [iconTab, setIconTab] = useState('Questions');
  const [dateVal, setDateVal] = useState(card.due_date ? new Date(card.due_date).toISOString().split('T')[0] : '');
  const [editingItemId, setEditingItemId] = useState(null);
  const [editItemText, setEditItemText] = useState('');
  const [menuItemId, setMenuItemId] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiResult, setAiResult] = useState(card.ai_analysis || null);
  const [showAiPanel, setShowAiPanel] = useState(false);
  const descRef = useRef(null);
  const fileRef = useRef(null);

  useEffect(() => { setLocalItems(card.card_items || []); }, [card.card_items]);
  useEffect(() => { setDesc(card.description || ''); }, [card.description]);
  useEffect(() => { if (editingDesc && descRef.current) descRef.current.focus(); }, [editingDesc]);

  const closeAllPopups = () => { setShowPriority(false); setShowCalendar(false); setShowAttach(false); setShowIconDeck(false); setMenuItemId(null); setShowAiPanel(false); };

  const askAI = async () => {
    closeAllPopups();
    setShowAiPanel(true);
    if (aiResult) return; // Already have analysis
    setAiLoading(true);
    try {
      const resp = await apiPostUtil(`/strategy-coach/card-ai/${card.card_id}`, {});
      if (resp.ok) {
        const data = await resp.json();
        setAiResult(data);
      } else {
        toast.error('AI analysis failed');
        setShowAiPanel(false);
      }
    } catch {
      toast.error('Connection error');
      setShowAiPanel(false);
    }
    setAiLoading(false);
  };

  const updateCard = (field, value) => {
    const newLists = board.lists.map(l => ({ ...l, cards: l.cards.map(c => c.card_id === card.card_id ? { ...c, [field]: value } : c) }));
    setBoard({ ...board, lists: newLists });
  };

  const saveDescription = async () => {
    setEditingDesc(false);
    if (desc === (card.description || '')) return;
    updateCard('description', desc);
    try {
      const resp = await apiPatch(`/cards/${card.card_id}`, { description: desc });
      if (!resp.ok) { updateCard('description', card.description || ''); toast.error('Failed to save'); }
    } catch { updateCard('description', card.description || ''); }
  };

  const setPriority = async (value) => {
    setShowPriority(false);
    updateCard('priority', value);
    try {
      const resp = await apiPatch(`/cards/${card.card_id}`, { priority: value });
      if (!resp.ok) { updateCard('priority', card.priority); toast.error('Failed'); }
    } catch { updateCard('priority', card.priority); }
  };

  const saveDate = async () => {
    setShowCalendar(false);
    if (!dateVal) return;
    const isoDate = new Date(dateVal).toISOString();
    updateCard('due_date', isoDate);
    try {
      const resp = await apiPatch(`/cards/${card.card_id}`, { due_date: isoDate });
      if (!resp.ok) { updateCard('due_date', card.due_date); toast.error('Failed'); }
    } catch { updateCard('due_date', card.due_date); }
  };

  // Add card item (NOT checklist)
  const addItem = async () => {
    if (!itemText.trim()) return;
    const content = itemText.trim();
    setItemText('');

    const optimisticItem = { item_id: 'temp_' + Date.now(), icon: selectedIcon, content, created_at: new Date().toISOString() };
    const prevItems = [...localItems];
    const newItems = [...prevItems, optimisticItem];
    setLocalItems(newItems);
    updateCard('card_items', newItems);

    try {
      const resp = await apiPost(`/cards/${card.card_id}/items`, { content, icon: selectedIcon });
      if (resp.ok) {
        const savedItem = await resp.json();
        const finalItems = newItems.map(i => i.item_id === optimisticItem.item_id ? savedItem : i);
        setLocalItems(finalItems);
        updateCard('card_items', finalItems);
      } else {
        setLocalItems(prevItems);
        updateCard('card_items', prevItems);
        toast.error('Failed to add item');
      }
    } catch {
      setLocalItems(prevItems);
      updateCard('card_items', prevItems);
      toast.error('Failed to add item');
    }
  };

  // Edit card item
  const saveEditItem = async (itemId) => {
    if (!editItemText.trim()) { setEditingItemId(null); return; }
    const prevItems = [...localItems];
    const updated = localItems.map(i => i.item_id === itemId ? { ...i, content: editItemText.trim() } : i);
    setLocalItems(updated);
    updateCard('card_items', updated);
    setEditingItemId(null);

    try {
      const resp = await apiPatch(`/cards/${card.card_id}/items/${itemId}`, { content: editItemText.trim() });
      if (!resp.ok) { setLocalItems(prevItems); updateCard('card_items', prevItems); toast.error('Failed to update'); }
    } catch { setLocalItems(prevItems); updateCard('card_items', prevItems); }
  };

  // Delete card item
  const deleteItem = async (itemId) => {
    const prevItems = [...localItems];
    const filtered = localItems.filter(i => i.item_id !== itemId);
    setLocalItems(filtered);
    updateCard('card_items', filtered);
    setMenuItemId(null);

    try {
      const resp = await apiDelete(`/cards/${card.card_id}/items/${itemId}`);
      if (!resp.ok) { setLocalItems(prevItems); updateCard('card_items', prevItems); toast.error('Failed to delete'); }
    } catch { setLocalItems(prevItems); updateCard('card_items', prevItems); }
  };

  // Change item icon
  const changeItemIcon = async (itemId, newIcon) => {
    const prevItems = [...localItems];
    const updated = localItems.map(i => i.item_id === itemId ? { ...i, icon: newIcon } : i);
    setLocalItems(updated);
    updateCard('card_items', updated);
    setMenuItemId(null);

    try {
      const resp = await apiPatch(`/cards/${card.card_id}/items/${itemId}`, { icon: newIcon });
      if (!resp.ok) { setLocalItems(prevItems); updateCard('card_items', prevItems); }
    } catch { setLocalItems(prevItems); updateCard('card_items', prevItems); }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setShowAttach(false);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const resp = await apiPostUtil(`/cards/${card.card_id}/attachments`, formData);
      if (resp.ok) {
        const data = await resp.json();
        updateCard('attachments', [...(card.attachments || []), data]);
        toast.success('File attached');
      }
    } catch { toast.error('Upload failed'); }
  };

  return (
    <div className="flip-card-back p-3 bg-background rounded-lg border border-odapto-orange/50 shadow-sm flex flex-col overflow-visible" onClick={(e) => e.stopPropagation()}>
      <input ref={fileRef} type="file" className="hidden" onChange={handleFileUpload} />

      {/* Header */}
      <div className="flex items-start gap-2 mb-2">
        <div className="shrink-0 w-8 h-8 rounded-full bg-white dark:bg-zinc-800 shadow-sm flex items-center justify-center mt-0.5">
          <img src={card.card_icon || '/icons/default-question.svg'} alt="" className="w-5 h-5 object-contain" />
        </div>
        <span className="flex-1 text-sm font-semibold text-odapto-orange min-w-0 line-clamp-2 break-words">{card.title}</span>
        <button onClick={() => setFlippedCards(prev => { const n = new Set(prev); n.delete(card.card_id); return n; })} className="shrink-0 p-1 rounded text-odapto-orange hover:bg-muted transition-colors" data-testid={`flip-back-btn-${card.card_id}`} title="Flip back">
          <RefreshCw className="w-4 h-4" />
        </button>
        <button onClick={() => setSelectedCard(card)} className="shrink-0 p-1 rounded text-muted-foreground hover:text-foreground hover:bg-muted transition-colors" title="Expand">
          <img src="/icons/large-window.svg" alt="Expand" className="w-4 h-4 dark:invert" />
        </button>
      </div>

      {/* Description */}
      {editingDesc ? (
        <textarea
          ref={descRef}
          value={desc}
          onChange={(e) => setDesc(e.target.value)}
          onBlur={saveDescription}
          onKeyDown={(e) => { if (e.key === 'Escape') saveDescription(); }}
          className="text-xs leading-relaxed mb-2 p-2 border border-odapto-orange/30 rounded-lg outline-none focus:border-odapto-orange resize-none bg-transparent min-h-[48px]"
          placeholder="Write a description..."
        />
      ) : (
        <p onClick={() => setEditingDesc(true)} className={`text-xs leading-relaxed mb-2 p-1.5 rounded-lg cursor-text hover:bg-muted/50 transition-colors min-h-[28px] ${desc ? 'text-foreground' : 'italic text-muted-foreground'}`}>
          {desc || 'Click to add description...'}
        </p>
      )}

      {/* Card Items (strategic notes/ideas — NOT checklist) */}
      {localItems.length > 0 && (
        <div className="space-y-1 mb-2">
          {localItems.map((item) => (
            <div key={item.item_id} className="group/item flex items-start gap-2 py-0.5 rounded hover:bg-muted/30 px-1 -mx-1 relative">
              {editingItemId === item.item_id ? (
                <>
                  <img src={item.icon || selectedIcon} alt="" className="w-4 h-4 shrink-0 mt-0.5" />
                  <input
                    type="text"
                    value={editItemText}
                    onChange={(e) => setEditItemText(e.target.value)}
                    onBlur={() => saveEditItem(item.item_id)}
                    onKeyDown={(e) => { if (e.key === 'Enter') saveEditItem(item.item_id); if (e.key === 'Escape') setEditingItemId(null); }}
                    className="flex-1 text-xs bg-transparent border-b border-odapto-orange/50 outline-none py-0.5 min-w-0"
                    autoFocus
                  />
                </>
              ) : (
                <>
                  <img src={item.icon || '/icons/business/target.svg'} alt="" className="w-4 h-4 shrink-0 mt-0.5" />
                  <span className="flex-1 text-xs text-foreground min-w-0">{item.content}</span>
                  <button
                    onClick={(e) => { e.stopPropagation(); setMenuItemId(menuItemId === item.item_id ? null : item.item_id); }}
                    className="shrink-0 p-0.5 rounded opacity-0 group-hover/item:opacity-100 text-muted-foreground hover:text-foreground transition-all"
                  >
                    <MoreVertical className="w-3 h-3" />
                  </button>
                </>
              )}

              {/* Item context menu */}
              {menuItemId === item.item_id && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setMenuItemId(null)} />
                  <div className="absolute right-0 top-full z-50 bg-popover border border-border rounded-lg shadow-lg py-1 w-32">
                    <button onClick={() => { setEditingItemId(item.item_id); setEditItemText(item.content); setMenuItemId(null); }} className="w-full text-left px-3 py-1.5 text-xs hover:bg-muted flex items-center gap-2">
                      <Pencil className="w-3 h-3" /> Edit
                    </button>
                    <button onClick={() => deleteItem(item.item_id)} className="w-full text-left px-3 py-1.5 text-xs hover:bg-muted flex items-center gap-2 text-destructive">
                      <Trash2 className="w-3 h-3" /> Delete
                    </button>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Add item input */}
      <div className="flex items-center gap-1.5 mb-3 relative">
        <button
          onClick={() => { closeAllPopups(); setShowIconDeck(true); }}
          className="w-6 h-6 rounded-full bg-amber-100 flex items-center justify-center shrink-0 cursor-pointer hover:bg-amber-200 transition-colors"
          title="Choose icon"
        >
          <span className="text-amber-600 font-bold text-sm">+</span>
        </button>

        {showIconDeck && (
          <>
            <div className="fixed inset-0 z-40" onClick={() => setShowIconDeck(false)} />
            <div className="absolute bottom-full left-0 mb-1 z-50 bg-popover border border-border rounded-lg shadow-lg p-3 w-64">
              <p className="text-xs font-semibold mb-2">Choose Item Icon</p>
              <div className="flex flex-wrap gap-1 mb-2 border-b border-border pb-2">
                {ICON_CATEGORIES.map(cat => (
                  <button key={cat.name} onClick={() => setIconTab(cat.name)} className={`px-2 py-1 text-[10px] font-medium rounded-full ${iconTab === cat.name ? 'bg-odapto-orange text-white' : 'bg-muted text-muted-foreground hover:bg-muted/80'}`}>
                    {cat.name}
                  </button>
                ))}
              </div>
              <div className="grid grid-cols-5 gap-2">
                {ICON_CATEGORIES.find(c => c.name === iconTab)?.icons.map(icon => (
                  <button
                    key={icon.path}
                    onClick={() => { setSelectedIcon(icon.path); setShowIconDeck(false); }}
                    className={`p-1.5 rounded-lg border-2 hover:shadow-sm transition-all ${selectedIcon === icon.path ? 'border-odapto-orange bg-odapto-orange/10' : 'border-transparent hover:border-zinc-300'}`}
                    title={icon.label}
                  >
                    <img src={icon.path} alt={icon.label} className="w-7 h-7 object-contain mx-auto" />
                  </button>
                ))}
              </div>
            </div>
          </>
        )}

        <input
          type="text"
          value={itemText}
          onChange={(e) => setItemText(e.target.value)}
          placeholder="Add more item"
          className="flex-1 text-xs bg-transparent border border-odapto-orange/30 rounded-full px-3 py-1.5 outline-none focus:border-odapto-orange placeholder:text-muted-foreground min-w-0"
          onKeyDown={(e) => { if (e.key === 'Enter') addItem(); }}
          data-testid={`add-item-${card.card_id}`}
        />
        <button
          onClick={addItem}
          disabled={!itemText.trim()}
          className="shrink-0 w-7 h-7 rounded-full bg-odapto-orange text-white flex items-center justify-center hover:bg-odapto-orange-hover disabled:opacity-30 disabled:cursor-not-allowed transition-all"
          title="Submit item"
          data-testid={`submit-item-${card.card_id}`}
        >
          <ArrowRight className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Toolbar */}
      <div className="flex items-stretch border border-border rounded-lg overflow-visible mt-auto relative">
        {/* Calendar */}
        <div className="relative flex-1 border-r border-border">
          <button onClick={() => { closeAllPopups(); setShowCalendar(true); }} className="w-full p-3 hover:bg-muted transition-colors flex items-center justify-center" title="Set Date">
            <img src="/icons/set-date.svg" alt="Date" className="w-7 h-7 dark:invert" />
          </button>
          {showCalendar && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setShowCalendar(false)} />
              <div className="absolute bottom-full left-0 mb-1 z-50 bg-popover border border-border rounded-lg shadow-lg p-3 w-52">
                <p className="text-xs font-semibold mb-2">Set Due Date</p>
                <input type="date" value={dateVal} onChange={(e) => setDateVal(e.target.value)} className="w-full text-xs border border-border rounded-lg px-2 py-1.5 bg-background" />
                <button onClick={saveDate} className="mt-2 w-full text-xs bg-odapto-orange text-white rounded-lg py-1.5 font-medium hover:bg-odapto-orange-hover">Save</button>
              </div>
            </>
          )}
        </div>

        {/* Ask AI */}
        <div className="relative flex-1 border-r border-border">
          <button onClick={askAI} disabled={aiLoading} className={`w-full p-2 hover:bg-muted transition-colors flex items-center justify-center ${showAiPanel ? 'bg-odapto-orange/10' : ''}`} title="Ask AI" data-testid={`ask-ai-btn-${card.card_id}`}>
            {aiLoading ? <Loader2 className="w-5 h-5 animate-spin text-odapto-orange" /> : <Lottie animationData={aiBrainAnimation} loop autoplay style={{ width: 32, height: 32 }} />}
          </button>
        </div>

        {/* Attachment */}
        <div className="relative flex-1 border-r border-border">
          <button onClick={() => { closeAllPopups(); setShowAttach(true); }} className="w-full p-3 hover:bg-muted transition-colors flex items-center justify-center" title="Add Attachment">
            <img src="/icons/attachment.svg" alt="Attach" className="w-7 h-7 dark:invert" />
          </button>
          {showAttach && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setShowAttach(false)} />
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 z-50 bg-popover border border-border rounded-lg shadow-lg py-1 w-48">
                <p className="text-xs font-semibold px-3 py-1.5 border-b border-border">Add Attachment</p>
                {ATTACH_OPTIONS.map(opt => (
                  <button key={opt.key} onClick={() => { setShowAttach(false); if (opt.key === 'upload' || opt.key === 'mobile') { fileRef.current?.click(); } else { setSelectedCard({ ...card, _openPicker: opt.key }); } }} className="w-full text-left px-3 py-2 text-xs hover:bg-muted flex items-center gap-2">
                    {opt.img ? <img src={opt.img} alt="" className="w-4 h-4" /> : <opt.icon className="w-4 h-4 text-muted-foreground" />}
                    {opt.label}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Priority */}
        <div className="relative flex-1">
          <button onClick={() => { closeAllPopups(); setShowPriority(true); }} className="w-full p-3 hover:bg-muted transition-colors flex items-center justify-center" title="Set Priority">
            <img src="/icons/set-priority.svg" alt="Priority" className="w-7 h-7 dark:invert" />
          </button>
          {showPriority && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setShowPriority(false)} />
              <div className="absolute bottom-full right-0 mb-1 z-50 bg-popover border border-border rounded-lg shadow-lg py-1 w-36">
                <p className="text-xs font-semibold px-3 py-1.5 border-b border-border">Set Priority</p>
                {PRIORITIES.map(p => (
                  <button key={p.value} onClick={() => setPriority(p.value)} className="w-full text-left px-3 py-2 text-xs hover:bg-muted flex items-center gap-2">
                    <span className={`w-3 h-3 rounded-full ${p.cls}`} /> {p.label}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* AI Analysis Panel */}
      {showAiPanel && (
        <div className="mt-2 p-3 rounded-lg border border-odapto-orange/30 bg-gradient-to-b from-odapto-orange/5 to-transparent relative" data-testid={`ai-panel-${card.card_id}`}>
          <button onClick={() => setShowAiPanel(false)} className="absolute top-2 right-2 p-0.5 rounded hover:bg-muted"><X className="w-3 h-3" /></button>
          <div className="flex items-center gap-1.5 mb-2">
            <Sparkles className="w-3.5 h-3.5 text-odapto-orange" />
            <span className="text-[10px] font-bold text-odapto-orange uppercase tracking-wider">AI Insight</span>
            {aiResult?.confidence && <span className="ml-auto text-[9px] text-muted-foreground">{aiResult.confidence}% confidence</span>}
          </div>

          {aiLoading && (
            <div className="flex items-center gap-2 py-3 justify-center text-muted-foreground">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span className="text-xs">Analyzing card...</span>
            </div>
          )}

          {aiResult && !aiLoading && (
            <div className="space-y-2 text-[11px]">
              {aiResult.strategic_insight && (
                <p className="font-medium text-foreground leading-relaxed">{aiResult.strategic_insight}</p>
              )}
              {aiResult.recommendations?.length > 0 && (
                <div>
                  <p className="font-semibold text-[10px] text-muted-foreground uppercase mb-1">Recommendations</p>
                  <ul className="space-y-0.5">
                    {aiResult.recommendations.map((r, i) => (
                      <li key={i} className="flex items-start gap-1.5 text-foreground">
                        <span className="text-odapto-orange mt-0.5">→</span><span>{r}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {aiResult.risks?.length > 0 && (
                <div>
                  <p className="font-semibold text-[10px] text-red-500 uppercase mb-1">Watch Out</p>
                  <ul className="space-y-0.5">
                    {aiResult.risks.map((r, i) => <li key={i} className="text-foreground">⚠ {r}</li>)}
                  </ul>
                </div>
              )}
              {aiResult.success_tips && (
                <p className="text-muted-foreground italic border-t border-border pt-1.5 mt-1.5">{aiResult.success_tips}</p>
              )}
              <button
                onClick={() => { setAiResult(null); askAI(); }}
                className="text-[10px] text-odapto-orange hover:underline mt-1"
              >
                Regenerate analysis
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
